/**
 * NaraChat Context Publishing System
 *
 * Components publish their current entity context (patient, cita, chatbot)
 * so NaraChat can include it in API calls for deeper contextual awareness.
 *
 * Usage:
 *   import { publishNaraContext, clearNaraContext } from '../naraChatContext';
 *   publishNaraContext('paciente', { id: 123, nombre: 'María García', edad: '45 años', ... });
 *   clearNaraContext();
 */

// Global store
let _currentContext = null;
const _listeners = new Set();

export const publishNaraContext = (type, data) => {
  _currentContext = { type, data, timestamp: Date.now() };
  _listeners.forEach(fn => fn(_currentContext));
};

export const clearNaraContext = () => {
  _currentContext = null;
  _listeners.forEach(fn => fn(null));
};

export const getNaraContext = () => _currentContext;

export const subscribeNaraContext = (fn) => {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
};

/**
 * Build a human-readable context string from the current entity
 */
export const buildContextString = (ctx) => {
  if (!ctx) return null;
  const { type, data } = ctx;

  switch (type) {
    case 'paciente': {
      let str = `Paciente seleccionado: ${data.nombre || 'N/A'}`;
      if (data.edad) str += `, ${data.edad}`;
      if (data.sexo) str += `, ${data.sexo}`;
      if (data.telefono) str += `, Tel: ${data.telefono}`;
      if (data.expediente) str += `, Expediente: ${data.expediente}`;
      if (data.alergias) str += `, Alergias: ${data.alergias}`;
      if (data.id) str += ` (ID paciente: ${data.id})`;
      return str;
    }
    case 'cita': {
      let str = `Cita seleccionada: ${data.paciente || 'N/A'}`;
      if (data.fecha) str += `, Fecha: ${data.fecha}`;
      if (data.hora) str += ` a las ${data.hora}`;
      if (data.estado) str += `, Estado: ${data.estado}`;
      if (data.motivo) str += `, Motivo: ${data.motivo}`;
      return str;
    }
    case 'chatbot': {
      let str = `Chatbot seleccionado: ${data.nombre || 'N/A'}`;
      if (data.whatsapp) str += `, WhatsApp: ${data.whatsapp}`;
      if (data.tipo) str += `, Tipo: ${data.tipo}`;
      if (data.id) str += ` (ID chatbot: ${data.id})`;
      return str;
    }
    case 'conversacion': {
      let str = `Conversacion seleccionada: ${data.numero || 'N/A'}`;
      if (data.resultado) str += `, Resultado: ${data.resultado}`;
      if (data.fecha) str += `, Fecha: ${data.fecha}`;
      return str;
    }
    default:
      return null;
  }
};

/**
 * Opens NaraChat with a pre-filled question
 * Dispatches a custom event that NaraChat listens for
 */
export const askNara = (question) => {
  window.dispatchEvent(new CustomEvent('nara-ask', { detail: { question } }));
};
